import { useState } from 'preact/hooks';
import type { PageProps } from '../app';
import { pemDecodeAll } from '../core/bytes/encoding';
import { embeddedSctEntry, verifyScts, type EmbeddedSctContext, type SctVerdict } from '../core/sct/embedded';
import { parseCertificate } from '../core/x509/parse';
import { Badge } from '../ui/components/Badge';
import { CertSummary } from '../ui/components/CertSummary';
import { ErrorBox } from '../ui/components/ErrorBox';
import { Icon } from '../ui/components/Icon';
import { Card } from '../ui/components/Section';
import { SctTable } from '../ui/components/SctTable';
import { inclusionLinksFor } from '../ui/sctLinks';
import { ensureRegistry, registry } from '../ui/state/logs';
import { ChainDiscovery } from '../ui/components/ChainDiscovery';
import { HostFetch } from '../ui/components/HostFetch';
import { SAMPLE_CHAIN_PEM } from '../ui/sample';
import { usePageState } from '../ui/state/pageState';
import { href } from '../router';
import { Breadcrumbs } from '../ui/components/Breadcrumbs';
import type { Sct, SignedEntry } from '../core/rfc6962/types';
import { useInclusionChecks } from '../ui/hooks/useInclusionChecks';
import { Spinner } from '../ui/components/Spinner';
import { parseSctInput } from '../core/rfc6962/sctInput';

interface Outcome {
  ctx: EmbeddedSctContext;
  /** Embedded SCTs first, then any pasted (TLS / OCSP) ones. */
  scts: Sct[];
  /** What each SCT signs: the precert entry for embedded SCTs, the final certificate for pasted ones. */
  entries: Array<SignedEntry | undefined>;
  labels: Array<string | undefined>;
  verdicts: SctVerdict[];
  links: Array<string | undefined>;
  issuers: Uint8Array[];
}

export function SctInspectorPage(_props: PageProps) {
  const [text, setText] = usePageState('sct:text', '');
  const [extraText, setExtraText] = usePageState('sct:extra', '');
  const [showExtra, setShowExtra] = usePageState('sct:showExtra', false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = usePageState<unknown>('sct:err', undefined);
  const [out, setOut] = usePageState<Outcome | undefined>('sct:out', undefined);

  const inspect = async (extraIssuers: Uint8Array[] = [], pem: string = text) => {
    setBusy(true);
    setErr(undefined);
    try {
      await ensureRegistry();
      const blocks = pemDecodeAll(pem);
      if (blocks.length === 0) throw new Error('Paste at least one certificate in PEM (or base64 / hex DER).');
      const [leaf, ...rest] = blocks.map((b) => b.der) as [Uint8Array, ...Uint8Array[]];
      const issuers = [...rest, ...extraIssuers];
      const ctx = await embeddedSctEntry(leaf, issuers);
      const reg = registry.value;
      // SCTs delivered outside the certificate (TLS extension, OCSP stapling) sign the final certificate itself.
      const extra = parseSctInput(extraText);
      const x509: SignedEntry | undefined = ctx.leaf.hasPoison ? undefined : { type: 'x509', cert: leaf };
      const scts = [...ctx.leaf.scts, ...extra];
      const entries = [...ctx.leaf.scts.map(() => ctx.entry), ...extra.map(() => x509)];
      const labels = [...ctx.leaf.scts.map(() => undefined), ...extra.map(() => 'delivered outside the certificate')];
      const lookup = (id: Uint8Array) => reg.byLogId(id)?.key;
      const verdicts = [...(await verifyScts(ctx.leaf.scts, ctx.entry, lookup)), ...(await verifyScts(extra, x509, lookup))];
      const links = [...(await inclusionLinksFor(ctx.leaf.scts, ctx.entry)), ...(await inclusionLinksFor(extra, x509))];
      if (scts.length === 0) throw new Error(ctx.leaf.hasPoison ? 'This is a precertificate: it carries no SCTs itself. Inspect the final certificate, or paste the SCTs the CA returned for it under "SCTs delivered outside the certificate".' : 'This certificate has no embedded SCTs. If the server delivers them in the TLS handshake or via OCSP, paste them under "SCTs delivered outside the certificate".');
      setOut({ ctx, scts, entries, labels, verdicts, links, issuers });
    } catch (e) {
      setErr(e);
      setOut(undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main class="content page-narrow">
      <Breadcrumbs items={[{ label: 'Home', href: href('/') }, { label: 'SCT Inspector' }]} />
      <div class="page-head">
        <div>
          <h1>SCT Inspector</h1>
          <p class="lede">Paste a certificate to see which logs issued its Signed Certificate Timestamps, verify each signature with the log's key, and prove the certificate is really in those logs — an inclusion proof is fetched from every log and checked against its signed tree head.</p>
          <p class="small muted" style="margin-top:0.4rem">Only the logs that issued an SCT can be asked: a log looks entries up by <em>leaf hash</em>, which includes the log's own timestamp from the SCT — there is no lookup by certificate, and tiled logs have no lookup at all. Finding a certificate in logs that never gave it an SCT is what monitors such as crt.sh are for.</p>
        </div>
      </div>
      <div class="stack">
        <Card foot={<>Include the <strong>issuer (CA) certificate</strong> after the leaf: SCT signatures cover a hash of the issuer's key. Get a site's whole chain with <code>openssl s_client -connect host:443 -servername host -showcerts &lt;/dev/null</code>.</>}>
          <form onSubmit={(e) => { e.preventDefault(); void inspect(); }}>
            <label class="field">
              <span>Certificate (+ issuer) in PEM, base64 or hex DER</span>
              <textarea class="input" rows={9} value={text} onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} placeholder={'-----BEGIN CERTIFICATE-----\n(leaf certificate)\n-----END CERTIFICATE-----\n-----BEGIN CERTIFICATE-----\n(issuer certificate)\n-----END CERTIFICATE-----'} spellcheck={false} autofocus />
            </label>
            <div style="margin-top:0.6rem">
              {!showExtra ? (
                <button type="button" class="linklike small" onClick={() => setShowExtra(true)}><Icon name="chevronRight" size={12} /> SCTs delivered outside the certificate (TLS extension / OCSP) — optional</button>
              ) : (
                <label class="field">
                  <span>SCTs delivered outside the certificate <span class="hint">optional — base64 or hex of a TLS SignedCertificateTimestampList or of single SCTs (one per line), or add-chain JSON; these sign the final certificate</span></span>
                  <textarea class="input mono" rows={3} value={extraText} onInput={(e) => setExtraText((e.target as HTMLTextAreaElement).value)} placeholder="AJEAjwB2AK…" spellcheck={false} />
                </label>
              )}
            </div>
            <div class="form-actions">
              <button type="submit" class="btn btn-primary" disabled={busy || !text.trim()}><Icon name="shield" /> {busy ? 'Inspecting…' : 'Inspect & check inclusion'}</button>
              {text ? (
                <button type="button" class="btn btn-ghost" onClick={() => { setText(''); setOut(undefined); setErr(undefined); }}>Clear</button>
              ) : (
                <button type="button" class="btn btn-ghost" onClick={() => setText(SAMPLE_CHAIN_PEM)}><Icon name="file" /> Load an example (example.com)</button>
              )}
            </div>
            {!text && (
              <div style="margin-top:0.6rem">
                <HostFetch
                  onChain={(pem) => {
                    setText(pem);
                    void inspect([], pem);
                  }}
                />
              </div>
            )}
          </form>
        </Card>

        {err !== undefined && <ErrorBox error={err} />}
        {out && <Results out={out} text={text} onChain={(pem) => { setText(pem); void inspect([], pem); }} />}
      </div>
    </main>
  );
}

/** Signature verdicts plus a live inclusion check per SCT, with a one-line summary. */
function Results({ out, text, onChain }: { out: Outcome; text: string; onChain: (pem: string) => void }) {
  const { checks, summary, running, run } = useInclusionChecks('sct:checks', out.scts, out.entries, true);
  const allIn = summary.included === summary.total && summary.total > 0;
  return (
          <>
            {!running && summary.total > 0 && (
              <div class={`notice ${allIn ? 'ok' : summary.included > 0 ? 'info' : ''}`} role="status">
                <Icon name={allIn ? 'check' : 'shield'} size={14} />
                <span>
                  <strong>{summary.included === 0 ? 'Not provably in any log' : allIn ? `In all ${summary.total} logs` : `In ${summary.included} of ${summary.total} logs`}</strong>
                  {' — '}{summary.included > 0 ? 'inclusion proofs fetched and verified against each log\'s signed tree head' : 'no inclusion proof could be verified'}
                  {summary.pending > 0 && `; ${summary.pending} still within the merge delay or not checkable`}
                  {summary.problems > 0 && summary.included < summary.total && `; ${summary.problems} could not be confirmed`}
                </span>
                <button type="button" class="btn btn-sm btn-ghost" onClick={() => run(true)}><Icon name="refresh" /> Re-check</button>
              </div>
            )}
            <Card
              title={<><Icon name="shield" /> SCTs <span class="badge badge-muted">{out.scts.length}</span></>}
              actions={running ? <Spinner label="Checking inclusion…" /> : undefined}
              foot={out.ctx.entry ? <>Embedded SCTs are verified over the precertificate entry: TBSCertificate without the SCT list, plus issuer_key_hash <code>{out.ctx.entry.type === 'precert' ? out.ctx.entry.issuerKeyHash.toHex().slice(0, 16) + '…' : ''}</code>. "In the log?" fetches an inclusion proof for the entry and checks it against the log's current signed tree head.</> : undefined}
            >
              {out.ctx.problem && (
                <div class="notice" style="margin-bottom:0.75rem;display:block">
                  <div>{out.ctx.problem}</div>
                  {out.issuers.length === 0 && (
                    <div style="margin-top:0.5rem">
                      <ChainDiscovery chain={pemDecodeAll(text).map((b) => b.der)} label="Find the issuer" onChain={onChain} />
                    </div>
                  )}
                </div>
              )}
              <SctTable scts={out.scts} entries={out.entries} labels={out.labels} verdicts={out.verdicts} inclusionLinks={out.links} checks={checks} />
            </Card>
            <Card title={<><Icon name="file" /> Certificate {out.ctx.leaf.hasPoison && <Badge tone="warn">precertificate</Badge>} {out.issuers.length > 0 ? <Badge tone="muted">{out.issuers.length} issuer cert{out.issuers.length === 1 ? '' : 's'} provided</Badge> : <Badge tone="warn">no issuer provided</Badge>}</>}>
              <CertSummary cert={out.ctx.leaf} />
            </Card>
          </>
  );
}
